type FormContent = {
  form_id?: string
  answer_id?: string
  fields?: unknown[]
  submitted?: boolean
  submitted_fields?: Record<string, unknown>
  submitted_fields_redacted?: boolean
  answered?: boolean
  pending_submission?: boolean
  submission_error?: string
  pending_submission_fields?: Record<string, unknown>
  pending_submission_display_fields?: Record<string, unknown>
}

export type FormStateMessage = {
  role: string
  type?: string
  content: unknown
}

type SubmissionInput = {
  formId: string
  answerId: string
  formConfig: FormContent
  fields: Record<string, unknown>
  displayFields: Record<string, unknown>
}

type SubmissionAck = {
  formId: string
  answerId?: string
  submittedFields?: Record<string, unknown> | null
  queued?: boolean
  persisted?: boolean
  final?: boolean
  submittedFieldsRedacted?: boolean
}

type SubmissionFailure = {
  formId: string
  answerId?: string
  message: string
}

const formContent = (message: FormStateMessage): FormContent | null =>
  message.type === 'show_form' &&
  message.content &&
  typeof message.content === 'object' &&
  !Array.isArray(message.content)
    ? (message.content as FormContent)
    : null

const isIssuedForm = (content: FormContent | null) => content && content.submitted !== true
const isSubmittedForm = (content: FormContent | null) => content?.submitted === true

export const getFormMessageIdentity = (message: FormStateMessage): string | undefined => {
  const content = formContent(message)
  if (!content?.form_id) return undefined

  if (isSubmittedForm(content)) {
    return content.answer_id ? `answer:${content.form_id}:${content.answer_id}` : `answer:${content.form_id}`
  }

  return `issued:${content.form_id}`
}

export const clearFormRetryState = <T extends FormStateMessage>(messages: T[], formId: string): T[] =>
  messages.map(message => {
    const content = formContent(message)
    if (!isIssuedForm(content) || content?.form_id !== formId) return message

    return {
      ...message,
      content: {
        ...content,
        submission_error: undefined,
        pending_submission_fields: undefined,
        pending_submission_display_fields: undefined
      }
    }
  })

export const mergeHydratedFormMessages = <T extends FormStateMessage & { _id?: string }>(
  currentMessages: T[],
  hydratedMessages: T[]
): T[] => {
  const existingIds = new Set(currentMessages.map(message => message._id).filter(Boolean))
  const uniqueHydratedMessages = hydratedMessages.filter(message => message._id && !existingIds.has(message._id))
  const hydratedIssuedFormIds = new Set(
    uniqueHydratedMessages
      .map(message => formContent(message))
      .filter((content): content is FormContent => Boolean(isIssuedForm(content) && content?.form_id))
      .map(content => content.form_id as string)
  )
  const confirmedFormIds = new Set(
    [...currentMessages, ...uniqueHydratedMessages]
      .map(message => formContent(message))
      .filter((content): content is FormContent =>
        Boolean(isSubmittedForm(content) && content?.form_id && content.pending_submission !== true)
      )
      .map(content => content.form_id as string)
  )
  const interruptedPendingByFormId = new Map<string, FormContent>()
  currentMessages.forEach(message => {
    const content = formContent(message)
    if (
      isSubmittedForm(content) &&
      content?.form_id &&
      content.pending_submission === true &&
      hydratedIssuedFormIds.has(content.form_id) &&
      !confirmedFormIds.has(content.form_id)
    ) {
      interruptedPendingByFormId.set(content.form_id, content)
    }
  })
  const reconciledHydratedMessages = uniqueHydratedMessages.map(message => {
    const content = formContent(message)
    if (!isIssuedForm(content) || !content?.form_id) return message

    const interruptedPending = interruptedPendingByFormId.get(content.form_id)
    if (!interruptedPending) return message

    return {
      ...message,
      role: 'assistant',
      content: {
        ...content,
        answer_id: content.answer_id ?? interruptedPending.answer_id,
        answered: false,
        submitted: false,
        submission_error: 'Form submission was interrupted. Please try again.',
        pending_submission_fields: interruptedPending.pending_submission_fields ?? interruptedPending.submitted_fields,
        pending_submission_display_fields:
          interruptedPending.pending_submission_display_fields ?? interruptedPending.submitted_fields
      }
    }
  })
  const incomingFormIdentities = new Set(
    reconciledHydratedMessages
      .map(message => getFormMessageIdentity(message))
      .filter((identity): identity is string => !!identity)
  )
  const retainedMessages = currentMessages.filter(message => {
    const content = formContent(message)
    if (
      isSubmittedForm(content) &&
      content?.form_id &&
      content.pending_submission === true &&
      hydratedIssuedFormIds.has(content.form_id)
    ) {
      return false
    }

    const identity = getFormMessageIdentity(message)

    return !identity || !incomingFormIdentities.has(identity)
  })

  return synchronizeIssuedFormState([...reconciledHydratedMessages, ...retainedMessages])
}

export const synchronizeIssuedFormState = <T extends FormStateMessage>(messages: T[]): T[] => {
  const blockedFormIds = new Set<string>()
  messages.forEach(message => {
    const content = formContent(message)
    if (isSubmittedForm(content) && content?.form_id) {
      blockedFormIds.add(content.form_id)
    }
  })

  return messages.map(message => {
    const content = formContent(message)
    if (!isIssuedForm(content) || !content?.form_id || !blockedFormIds.has(content.form_id)) return message

    return {
      ...message,
      content: {
        ...content,
        answered: true
      }
    }
  })
}

export const addOptimisticFormSubmission = <T extends FormStateMessage>(messages: T[], input: SubmissionInput): T[] => {
  let replacedPendingAnswer = false

  const next = messages.map(message => {
    const content = formContent(message)
    if (!content || content.form_id !== input.formId) return message

    if (isIssuedForm(content)) {
      return {
        ...message,
        role: 'assistant',
        content: {
          ...content,
          answer_id: input.answerId,
          answered: true,
          submission_error: undefined,
          pending_submission_fields: undefined,
          pending_submission_display_fields: undefined
        }
      }
    }

    if (content.pending_submission === true) {
      replacedPendingAnswer = true

      return {
        ...message,
        role: 'user',
        content: {
          ...input.formConfig,
          form_id: input.formId,
          answer_id: input.answerId,
          type: 'show_form',
          answered: true,
          submitted: true,
          submitted_fields: input.displayFields,
          pending_submission: true,
          pending_submission_fields: input.fields,
          pending_submission_display_fields: input.displayFields
        }
      }
    }

    return message
  })

  const submittedMessage = {
    role: 'user',
    type: 'show_form',
    content: {
      ...input.formConfig,
      form_id: input.formId,
      answer_id: input.answerId,
      type: 'show_form',
      answered: true,
      submitted: true,
      submitted_fields: input.displayFields,
      pending_submission: true,
      pending_submission_fields: input.fields,
      pending_submission_display_fields: input.displayFields
    }
  } as unknown as T

  if (!replacedPendingAnswer) next.push(submittedMessage)

  return next
}

export const acknowledgeFormSubmission = <T extends FormStateMessage>(messages: T[], ack: SubmissionAck): T[] => {
  if (ack.queued !== false || ack.persisted !== true || ack.final !== true) return messages

  const pendingAnswers = messages
    .map(message => formContent(message))
    .filter(
      (content): content is FormContent =>
        content?.form_id === ack.formId && isSubmittedForm(content) && content.pending_submission === true
    )

  if (
    ack.answerId &&
    pendingAnswers.length > 0 &&
    !pendingAnswers.some(content => content.answer_id === ack.answerId)
  ) {
    return messages
  }

  if (ack.submittedFieldsRedacted === true) {
    let issuedConfig: FormContent | null = null
    let redactedMarkerAdded = false
    const redactedMessages: T[] = []

    messages.forEach(message => {
      const content = formContent(message)
      if (!content || content.form_id !== ack.formId) {
        redactedMessages.push(message)

        return
      }

      if (isIssuedForm(content)) {
        issuedConfig = content
        redactedMessages.push({
          ...message,
          role: 'assistant',
          content: {
            ...content,
            ...(ack.answerId ? { answer_id: ack.answerId } : {}),
            answered: true,
            submission_error: undefined,
            pending_submission_fields: undefined,
            pending_submission_display_fields: undefined
          }
        })

        return
      }

      if (redactedMarkerAdded) return
      redactedMarkerAdded = true
      issuedConfig ??= content
      redactedMessages.push({
        ...message,
        role: 'user',
        type: 'show_form',
        content: {
          ...content,
          form_id: ack.formId,
          ...(ack.answerId ? { answer_id: ack.answerId } : {}),
          answered: true,
          submitted: true,
          submitted_fields: {},
          submitted_fields_redacted: true,
          pending_submission: false,
          submission_error: undefined,
          pending_submission_fields: undefined,
          pending_submission_display_fields: undefined
        }
      })
    })

    if (!redactedMarkerAdded && issuedConfig) {
      redactedMessages.push({
        role: 'user',
        type: 'show_form',
        content: {
          ...issuedConfig,
          form_id: ack.formId,
          ...(ack.answerId ? { answer_id: ack.answerId } : {}),
          answered: true,
          submitted: true,
          submitted_fields: {},
          submitted_fields_redacted: true,
          pending_submission: false
        }
      } as unknown as T)
    }

    return synchronizeIssuedFormState(redactedMessages)
  }

  const hasConfirmedAnswer = messages.some(message => {
    const content = formContent(message)

    return content?.form_id === ack.formId && isSubmittedForm(content) && content.pending_submission !== true
  })

  if (hasConfirmedAnswer) {
    return synchronizeIssuedFormState(
      messages.filter(message => {
        const content = formContent(message)

        return !(content?.form_id === ack.formId && isSubmittedForm(content) && content.pending_submission === true)
      })
    )
  }

  let acknowledgedPending = false
  let sawPendingAnswer = false
  let issuedConfig: FormContent | null = null

  const next = messages.map(message => {
    const content = formContent(message)
    if (!content || content.form_id !== ack.formId) return message

    if (isIssuedForm(content)) {
      issuedConfig = content

      return {
        ...message,
        role: 'assistant',
        content: {
          ...content,
          ...(ack.answerId ? { answer_id: ack.answerId } : {}),
          answered: true,
          submission_error: undefined,
          pending_submission_fields: undefined,
          pending_submission_display_fields: undefined
        }
      }
    }

    if (content.pending_submission === true) sawPendingAnswer = true

    const matchesPending = content.pending_submission === true && (!ack.answerId || content.answer_id === ack.answerId)
    if (!matchesPending) return message

    acknowledgedPending = true
    const fields = ack.submittedFields ?? content.submitted_fields ?? content.pending_submission_display_fields

    return {
      ...message,
      role: 'user',
      content: {
        ...content,
        ...(ack.answerId ? { answer_id: ack.answerId } : {}),
        answered: true,
        submitted: true,
        submitted_fields: fields ?? {},
        pending_submission: false,
        submission_error: undefined,
        pending_submission_fields: undefined,
        pending_submission_display_fields: undefined
      }
    }
  })

  if (
    !acknowledgedPending &&
    !sawPendingAnswer &&
    issuedConfig &&
    ack.submittedFields &&
    Object.keys(ack.submittedFields).length > 0
  ) {
    next.push({
      role: 'user',
      type: 'show_form',
      content: {
        ...issuedConfig,
        form_id: ack.formId,
        ...(ack.answerId ? { answer_id: ack.answerId } : {}),
        answered: true,
        submitted: true,
        submitted_fields: ack.submittedFields,
        pending_submission: false
      }
    } as unknown as T)
  }

  return synchronizeIssuedFormState(next)
}

export const rejectFormSubmission = <T extends FormStateMessage>(messages: T[], failure: SubmissionFailure): T[] => {
  let retryFields: Record<string, unknown> | undefined
  let retryDisplayFields: Record<string, unknown> | undefined
  let removedPending = false

  const withoutPending = messages.filter(message => {
    const content = formContent(message)
    if (!content || content.form_id !== failure.formId || content.pending_submission !== true) return true
    if (failure.answerId && content.answer_id !== failure.answerId) return true

    removedPending = true
    retryFields = content.pending_submission_fields ?? content.submitted_fields
    retryDisplayFields = content.pending_submission_display_fields ?? content.submitted_fields

    return false
  })

  if (!removedPending) return synchronizeIssuedFormState(messages)

  const hasConfirmedAnswer = withoutPending.some(message => {
    const content = formContent(message)

    return content?.form_id === failure.formId && content.submitted === true && content.pending_submission !== true
  })

  if (hasConfirmedAnswer) return synchronizeIssuedFormState(withoutPending)

  return withoutPending.map(message => {
    const content = formContent(message)
    if (!isIssuedForm(content) || content?.form_id !== failure.formId) return message

    return {
      ...message,
      role: 'assistant',
      content: {
        ...content,
        answer_id: content.answer_id,
        answered: hasConfirmedAnswer,
        submission_error: failure.message,
        pending_submission_fields: retryFields,
        pending_submission_display_fields: retryDisplayFields
      }
    }
  })
}
